/**
 * Based on code from:
 * https://github.com/Kitware/VolView/blob/main/src/vtk/CrosshairsWidget/behavior.ts
 */

import macro from "vtk.js/Sources/macro";
import { vec3 } from "gl-matrix";

/**
 * Axis-aligned bounding box as [minX, maxX, minY, maxY, minZ, maxZ].
 * @typedef {[number, number, number, number, number, number]} Bounds
 */

/**
 * Clamp a 3D point to the given bounds.
 * @param {Bounds} bounds
 * @param {ReadonlyArray<number>} point
 * @returns {number[]} 3-element array [x, y, z]
 */
function clampPointToBounds(bounds, point) {
    return point.map((p, i) =>
        Math.max(bounds[i * 2], Math.min(bounds[i * 2 + 1], p)),
    );
}

/**
 * vtk widget behavior (no TypeScript).
 * @param {any} publicAPI
 * @param {any} model
 */
export default function widgetBehavior(publicAPI, model) {
    model.classHierarchy.push("vtkCrosshairsWidgetProp");

    // support setting per-view widget manipulators
    macro.setGet(publicAPI, model, ["manipulator"]);

    // --------------------------------------------------------------------------
    // Interactor events
    // --------------------------------------------------------------------------

    // --------------------------------------------------------------------------
    // Left press: Select handle to drag
    // --------------------------------------------------------------------------

    publicAPI.handleLeftButtonPress = (e) => {
        if (!model.pickable) {
            return macro.VOID;
        }
        model.widgetState.setDragging(true);
        model._interactor.requestAnimation(publicAPI);
        model._apiSpecificRenderWindow.setCursor("crosshairs");
        publicAPI.invokeStartInteractionEvent();
        publicAPI.handleMouseMove(e);
        return macro.EVENT_ABORT;
    };

    // --------------------------------------------------------------------------
    // Mouse move: Drag selected handle / Handle follows the mouse
    // --------------------------------------------------------------------------

    publicAPI.handleMouseMove = (callData) => {
        // technically need to call requestAnimation during the initial phase of placing
        // the crosshairs, but that's not needed since no VTK object is actually rendered.

        if (
            model.widgetState.getDragging() &&
            model.pickable &&
            model.manipulator
        ) {
            const { worldCoords: worldCoordsOfPointer } =
                model.manipulator.handleEvent(
                    callData,
                    model._apiSpecificRenderWindow,
                );

            const handle = model.widgetState.getHandle();
            const worldToIndex = model.widgetState.getWorldToIndex();
            const indexToWorld = model.widgetState.getIndexToWorld();

            if (worldToIndex.length && indexToWorld.length) {
                const indexCoordsOfPointer = vec3.create();
                const worldOrigin = vec3.create();
                const bounds = handle.getBounds();

                vec3.transformMat4(
                    indexCoordsOfPointer,
                    worldCoordsOfPointer,
                    worldToIndex,
                );

                const indexOrigin = clampPointToBounds(
                    bounds,
                    indexCoordsOfPointer,
                );
                vec3.transformMat4(worldOrigin, indexOrigin, indexToWorld);

                handle.setOrigin(worldOrigin);
                publicAPI.invokeInteractionEvent();
                return macro.EVENT_ABORT;
            }
        }

        return macro.VOID;
    };

    // --------------------------------------------------------------------------
    // Left release: Finish drag / Create new handle
    // --------------------------------------------------------------------------

    publicAPI.handleLeftButtonRelease = () => {
        if (model.widgetState.getDragging() && model.pickable) {
            model._interactor.cancelAnimation(publicAPI);
            model._apiSpecificRenderWindow.setCursor("default");
            publicAPI.invokeEndInteractionEvent();
        }
        model.widgetState.setDragging(false);
    };

    // --------------------------------------------------------------------------
    // Focus API - modeHandle follows mouse when widget has focus
    // --------------------------------------------------------------------------

    publicAPI.grabFocus = () => {};

    // --------------------------------------------------------------------------

    publicAPI.loseFocus = () => {};
}
